import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { friendlyAuthError } from "@/lib/auth-errors";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { trackFunnel } from "@/lib/funnel";
import {
  ACCOUNT_CHOICES,
  persistAccountChoice,
  type AccountChoice,
} from "@/lib/account-choices";
import { Check, ShieldCheck, Zap, Clock } from "lucide-react";

// Ad landing page. Single purpose: convert paid traffic into signups.
// Intentionally minimal — no nav, no footer beyond legal, no FAQ, no
// pricing matrix. One headline, social proof, account chooser with
// transparent pricing, Google primary + email fallback.
export const Route = createFileRoute("/start")({
  component: StartPage,
  head: () => ({
    meta: [
      { title: "Start in 30 seconds — MissionReady" },
      {
        name: "description",
        content:
          "Stand up your security operations center in 30 seconds. Free forever for individual officers. 30-day free trial on every paid plan — no credit card required.",
      },
      // Ad landing pages should not compete with the canonical /signup in search.
      { name: "robots", content: "noindex, follow" },
      ...socialMeta({
        title: "Start in 30 seconds — MissionReady",
        description: "Free forever for solo officers. 30-day free trial on every paid plan — no card required.",
        url: "https://missionready.dev/start",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/start" }],
  }),
});

function StartPage() {
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [choice, setChoice] = useState<AccountChoice["value"]>("individual");
  const [submitting, setSubmitting] = useState(false);
  const [formStarted, setFormStarted] = useState(false);

  const selected = ACCOUNT_CHOICES.find((c) => c.value === choice)!;

  useEffect(() => {
    trackFunnel("start_landing_viewed", {}, { once: true });
  }, []);

  useEffect(() => {
    if (!loading && session) navigate({ to: "/onboarding" });
  }, [session, loading, navigate]);

  useEffect(() => {
    persistAccountChoice(selected);
  }, [selected]);

  const markStarted = () => {
    if (!formStarted) {
      setFormStarted(true);
      trackFunnel("start_form_started", { account_type: selected.path });
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const redirectTo =
      typeof window !== "undefined"
        ? window.location.origin + "/onboarding"
        : undefined;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          account_type: selected.path,
          plan_preselect: selected.planKey,
        },
        emailRedirectTo: redirectTo,
      },
    });
    setSubmitting(false);
    if (error) {
      trackFunnel("start_signup_failed", { reason: error.message });
      return toast.error(friendlyAuthError(error));
    }
    trackFunnel("start_signup_completed", {
      user_id: data.user?.id ?? null,
      email_confirmed: !!data.user?.email_confirmed_at,
      has_session: !!data.session,
      account_type: selected.path,
      plan_preselect: selected.planKey,
    });
    toast.success("Account created");
  };

  return (
    <div className="min-h-dvh bg-hero">
      {/* Tiny top bar: just logo, no nav. Removes distractions. */}
      <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-5">
        <Logo />
        <Link
          to="/login"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Sign in
        </Link>
      </div>

      <main className="mx-auto grid max-w-5xl gap-10 px-4 pb-16 pt-4 md:grid-cols-[1.05fr_1fr] md:gap-12 md:pt-8">
        {/* Left: headline + bullets + trust. */}
        <section className="flex flex-col justify-center">
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground md:text-5xl">
            Run your security ops <span className="text-primary">in 30 seconds</span>.
          </h1>
          <p className="mt-4 max-w-md text-base text-muted-foreground md:text-lg">
            Live GPS tracking, SOS, time clock, patrols, incidents, and AI
            reporting — in one app. Free forever for solo officers.
          </p>

          <ul className="mt-6 space-y-2.5 text-sm">
            {[
              { icon: Zap, text: "One-tap Google signup — no forms" },
              { icon: Clock, text: "30-day free trial on every paid plan" },
              { icon: ShieldCheck, text: "No credit card. Cancel any time." },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2 text-foreground">
                <Icon className="h-4 w-4 text-primary" aria-hidden />
                {text}
              </li>
            ))}
          </ul>

          <p className="mt-8 text-xs text-muted-foreground">
            Used by patrol agencies and solo contractors across the US.
          </p>
        </section>

        {/* Right: signup card — Google primary, then chooser, then email. */}
        <section className="rounded-2xl border border-border bg-card p-6 shadow-card md:p-7">
          <h2 className="font-display text-xl font-semibold">Get started</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            No credit card. Cancel any time.
          </p>

          <div className="mt-5">
            <GoogleSignInButton label={`Continue with Google → ${selected.title}`} />
            <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
              Fastest way in — uses your Google email.
            </p>
          </div>

          {/* Account chooser with transparent pricing. */}
          <div className="mt-5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              I'm signing up as
            </Label>
            <div className="mt-2 grid gap-2">
              {ACCOUNT_CHOICES.map((opt) => {
                const active = choice === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${
                      active
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name="account_type"
                      value={opt.value}
                      checked={active}
                      onChange={() => {
                        markStarted();
                        setChoice(opt.value);
                      }}
                      className="mt-1 h-4 w-4 accent-primary"
                    />
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="text-sm font-medium text-foreground">
                          {opt.title}
                        </div>
                        <div className="text-right">
                          <div className="font-display text-sm font-semibold text-foreground">
                            {opt.price}
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            {opt.priceNote}
                          </div>
                        </div>
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {opt.desc}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              <Check className="mr-1 inline h-3 w-3 text-primary" />
              Change plan any time. Trials never auto-charge — you'll be asked
              first.
            </p>
          </div>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" />
            or use email
            <div className="h-px flex-1 bg-border" />
          </div>

          <form
            onSubmit={onSubmit}
            onFocus={markStarted}
            className="space-y-3"
            autoComplete="on"
          >
            <div>
              <Label htmlFor="s_name" className="sr-only">
                Full name
              </Label>
              <Input
                id="s_name"
                name="name"
                autoComplete="name"
                placeholder="Full name"
                required
                value={fullName}
                onChange={(e) => {
                  markStarted();
                  setFullName(e.target.value);
                }}
              />
            </div>
            <div>
              <Label htmlFor="s_email" className="sr-only">
                Email
              </Label>
              <Input
                id="s_email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Work email"
                required
                value={email}
                onChange={(e) => {
                  markStarted();
                  setEmail(e.target.value);
                }}
              />
            </div>
            <div>
              <Label htmlFor="s_password" className="sr-only">
                Password
              </Label>
              <Input
                id="s_password"
                name="password"
                type="password"
                autoComplete="new-password"
                placeholder="Password (8+ chars)"
                required
                minLength={8}
                value={password}
                onChange={(e) => {
                  markStarted();
                  setPassword(e.target.value);
                }}
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? "Creating…" : `Create account — ${selected.price}`}
            </Button>
          </form>

          <p className="mt-4 text-center text-[11px] text-muted-foreground">
            By signing up you agree to our{" "}
            <Link to="/terms" className="underline hover:text-foreground">
              terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy-policy" className="underline hover:text-foreground">
              privacy policy
            </Link>
            .
          </p>
        </section>
      </main>
    </div>
  );
}
